/**
 * Build a COMPACT, length-capped JSON envelope of line items for a document
 * header's `description` column.
 *
 * Why: the document header (SalesInvoice / PurchaseOrder / POS sale) keeps a
 * copy of its line items in `description` JSON as a *recoverable fallback* —
 * if the separate `*Line` table POST fails (a transient 401), Edit can still
 * re-hydrate the items from here. BUT that `description` column has a small,
 * undocumented size limit (not in swagger). A bulky envelope overflows it and
 * the WHOLE header POST 500s with "String or binary data would be truncated"
 * — which breaks saving entirely, far worse than losing the fallback.
 *
 * So we (a) trim each line to only the keys the recovery readers use
 * (name / quantity / unitPrice / taxRate / productId), and (b) drop trailing
 * lines until the JSON fits under `cap`. The separate line table remains the
 * authoritative store for the full, untrimmed lines.
 */
export interface DescLineInput {
  name?: unknown;
  quantity?: unknown;
  unitPrice?: unknown;
  taxRate?: unknown;
  productId?: unknown;
}

export function compactDescEnvelope(lines: DescLineInput[], cap = 450): string {
  const rows = lines.map((l) => ({
    name: String(l.name ?? '').slice(0, 48),
    quantity: Number(l.quantity) || 0,
    unitPrice: Number(l.unitPrice) || 0,
    taxRate: Number(l.taxRate) || 0,
    productId: (l.productId ?? null) as number | string | null,
  }));
  let truncated = false;
  let json = JSON.stringify({ lines: rows });
  // Keep the JSON safely under the column limit. Dropping trailing lines only
  // shrinks the FALLBACK — the *Line table still holds every line.
  while (json.length > cap && rows.length > 0) {
    rows.pop();
    truncated = true;
    json = JSON.stringify({ lines: rows, truncated: true });
  }
  return truncated ? json : JSON.stringify({ lines: rows });
}
